import { useEffect, useRef, useState } from 'react';
import {
  simulateAvatarFactorySchedule,
  streamAvatarFactoryPreviewChat,
  type AvatarFactoryDraftDetail,
  type AvatarFactoryPreviewMessage,
} from '../../lib/admin-avatar-factory-api';
import { translateAvatarFactoryStatus, type AvatarFactoryCopy } from './i18n';

interface PreviewChatPanelProps {
  accessToken: string;
  copy: AvatarFactoryCopy;
  draft: AvatarFactoryDraftDetail;
  onDraftRefresh: () => Promise<void>;
  onError: (message: string) => void;
}

interface LocalPreviewMessage extends AvatarFactoryPreviewMessage {
  isEphemeral?: boolean;
}

function getInitialMessages(draft: AvatarFactoryDraftDetail): LocalPreviewMessage[] {
  if (draft.previewMessages.length > 0) {
    return draft.previewMessages;
  }

  const greeting = draft.greeting.trim();
  if (!greeting) {
    return [];
  }

  return [
    {
      id: `greeting-${draft.id}`,
      role: 'assistant',
      content: greeting,
      messageKind: 'greeting',
      metadataJson: {},
      createdAt: draft.updatedAt,
      isEphemeral: true,
    },
  ];
}

export default function PreviewChatPanel({
  accessToken,
  copy,
  draft,
  onDraftRefresh,
  onError,
}: PreviewChatPanelProps) {
  const localMessageCounter = useRef(0);
  const [messages, setMessages] = useState<LocalPreviewMessage[]>(() => getInitialMessages(draft));
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [failedUserMessage, setFailedUserMessage] = useState<string | null>(null);

  useEffect(() => {
    setMessages(getInitialMessages(draft));
  }, [draft]);

  const sendMessage = async (retryText?: string) => {
    const messageText = (retryText ?? inputValue).trim();
    if (!messageText || isSending) {
      return;
    }

    localMessageCounter.current += 1;
    const messageSequence = localMessageCounter.current;
    const userMessageId = `local-user-${draft.id}-${messageSequence}`;
    const assistantMessageId = `local-assistant-${draft.id}-${messageSequence}`;
    const userMessage: LocalPreviewMessage = {
      id: userMessageId,
      role: 'user',
      content: messageText,
      messageKind: 'chat',
      metadataJson: {},
      createdAt: new Date().toISOString(),
    };
    const assistantMessage: LocalPreviewMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      messageKind: 'chat',
      metadataJson: {},
      createdAt: new Date().toISOString(),
    };

    if (!retryText) {
      setInputValue('');
    }
    setFailedUserMessage(null);
    setMessages((items) => [...items, userMessage, assistantMessage]);
    setIsSending(true);

    try {
      await streamAvatarFactoryPreviewChat(accessToken, draft.id, {
        message: messageText,
        onText: (chunk) => {
          setMessages((items) =>
            items.map((item) =>
              item.id === assistantMessageId ? { ...item, content: `${item.content}${chunk}` } : item,
            ),
          );
        },
        onError: (message) => {
          setFailedUserMessage(messageText);
          onError(message);
        },
        onDone: () => {
          void onDraftRefresh().catch((error) => {
            const refreshMessage =
              error instanceof Error && error.message.trim()
                ? error.message
                : copy.preview.refreshFallback;
            onError(refreshMessage);
          });
        },
      });
    } finally {
      setIsSending(false);
    }
  };

  const simulateNow = async () => {
    if (isSimulating) {
      return;
    }

    setIsSimulating(true);
    try {
      const simulatedMessage = await simulateAvatarFactorySchedule(accessToken, draft.id);
      setMessages((items) => [...items, simulatedMessage]);
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message
          : copy.preview.simulateFallback;
      onError(message);
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <>
      <div className="avatar-factory-preview-head">
        <p className="avatar-factory-panel-kicker">{copy.preview.eyebrow}</p>
        <h2>{copy.preview.title}</h2>
        <p className="avatar-factory-preview-name">{draft.displayName}</p>
        <div className="avatar-factory-preview-meta" aria-label={copy.preview.statusAria}>
          <p>{copy.preview.revision(draft.previewRevision)}</p>
          <p>{copy.preview.generationStatus(translateAvatarFactoryStatus(copy, draft.generationStatus))}</p>
        </div>
      </div>

      <div className="avatar-factory-chat-stream" aria-live="polite">
        {messages.map((message) => (
          <div
            key={message.id}
            className="avatar-factory-chat-row"
            data-role={message.role}
            data-message-kind={message.messageKind}
          >
            <span className="avatar-factory-chat-avatar" aria-hidden="true">
              {message.role === 'user' ? 'U' : 'A'}
            </span>
            <div>
              <p className="avatar-factory-chat-speaker">
                {message.role === 'user' ? copy.preview.userSpeaker : copy.preview.agentSpeaker}
              </p>
              <div className="avatar-factory-chat-bubble" data-role={message.role}>
                {message.messageKind === 'simulated_schedule' ? (
                  <p className="avatar-factory-message-label">{copy.preview.simulatedSchedule}</p>
                ) : null}
                <p>{message.content}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="avatar-factory-preview-composer">
        <label htmlFor="avatar-factory-preview-message">{copy.preview.messageLabel}</label>
        <textarea
          id="avatar-factory-preview-message"
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
          }}
          rows={3}
          placeholder={copy.preview.placeholder}
        />

        <div className="avatar-factory-preview-actions">
          <button
            type="button"
            onClick={() => {
              void sendMessage();
            }}
            disabled={isSending || !inputValue.trim()}
          >
            {isSending ? copy.preview.sending : copy.preview.send}
          </button>
          {failedUserMessage ? (
            <button
              type="button"
              onClick={() => {
                void sendMessage(failedUserMessage);
              }}
              disabled={isSending}
            >
              {copy.page.retry}
            </button>
          ) : null}
        </div>
      </div>

      <div className="avatar-factory-simulate-card">
        <button type="button" onClick={() => void simulateNow()} disabled={isSimulating}>
          {isSimulating ? copy.preview.simulating : copy.preview.simulateNow}
        </button>
        <p>{copy.preview.helper}</p>
      </div>
    </>
  );
}
